import React from "react";
import { Link, NavLink } from "react-router-dom";
import { DocumentNode, gql, useQuery } from "@apollo/client";

import fsciLogo from "src/assets/images/fsci_report_logo.png";
import ffiLogo from "src/assets/images/ffi_report_logo.svg";
import "./report_tabs_combined.scss";
import { PrintIcon } from "../../assets/icons/print_icon";
import { Report, ReportEdge, School, Viewer } from "src/generated/types";

export const REPORTS_DETAIL_QUERY = (type: string): DocumentNode => {
    let version = "2";

    if (type === "ffi") {
        version = "1";
    }

    return gql`
    query ReportsDetail($type: String!) {
        viewer {
            user {
                school {
                    id
                    name
                    reports(
                        where: { results: { exists: true }, version: { equalTo: "${version}" }, type: { equalTo: $type } }
                        order: [year_DESC]
                    ) {
                        edges {
                            node {
                                id
                                results
                                year
                                type
                            }
                        }
                    }
                }
            }
        }
    }
`;
};

interface ReportTabsCombinedProps {
    currentReports: ReportEdge[];
    readonly school: School;
}

interface ApolloQueryReportsData {
    readonly viewer: Viewer;
    readonly reports: Array<Report>;
}

interface MultiReportVars {
    readonly type: string;
}

const reportTypes = ["fsci", "ffi"];
const fsciName = "ACSI Flourishing School Culture Instrument";
const ffiName = "Flourishing Faith Index";

export const ReportTabsCombined = ({ currentReports, school }: ReportTabsCombinedProps): React.ReactElement => {
    // Set "searchType" to the opposite type of the current report (FSCI or FFI)
    // This will be used to check if the opposite report exists for the same year
    // If it doesn't, then permanently disable the corresponding tab in the UI
    const searchType = currentReports[0].node?.type === "fsci" ? "ffi" : "fsci";

    const { data, error } = useQuery<ApolloQueryReportsData, MultiReportVars>(REPORTS_DETAIL_QUERY(searchType), {
        variables: { type: searchType },
    });

    if (error) {
        console.error(error);
    }

    return (
        <div className="report-page-title-line">
            <div className="report-page-meta">
                <h2 className="report-page-subtitle">
                    {currentReports[0].node?.type === "fsci"
                        ? "ACSI Flourishing School Culture Instrument"
                        : "Flourishing Faith Index"}
                </h2>
                <Link
                    className="report-print"
                    to={{
                        pathname: `/reports/${currentReports[0].node?.type}/print`,
                        state: { school, currentReports },
                    }}
                >
                    <PrintIcon /> Print/Download
                </Link>
            </div>
            <div className="report-tabs">
                {reportTypes.map((reportType, index) => {
                    let report = null;
                    // If the current report is the reportType, set it to 'report'
                    if (currentReports[0].node?.type === reportType) {
                        report = currentReports[0].node;

                        // If the other report type exists for the year and it equals the reportType, set it to 'report'
                    } else if (
                        data?.viewer.user.school?.reports.edges &&
                        data?.viewer.user.school?.reports.edges[0]?.node?.type === reportType
                    ) {
                        report = data?.viewer.user.school?.reports.edges[0]?.node;
                    }
                    // In all other cases, report will be falsy (null)

                    if (report) {
                        return (
                            <NavLink
                                activeClassName="active-link"
                                className={`report-tab ${
                                    currentReports[0].node?.type === reportType ? "active-link" : ""
                                }`}
                                key={index}
                                to={`/reports/${report.type}/overview`}
                            >
                                <img
                                    alt={report.type === "fsci" ? fsciName : ffiName}
                                    src={report.type === "fsci" ? fsciLogo : ffiLogo}
                                />
                            </NavLink>
                        );
                    } else {
                        return (
                            <div className="report-tab" key={index}>
                                <img
                                    alt={reportType === "fsci" ? fsciName : ffiName}
                                    src={reportType === "fsci" ? fsciLogo : ffiLogo}
                                />
                            </div>
                        );
                    }
                })}
            </div>
        </div>
    );
};
